import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import celebrate from '../utils/celebrate';
import PageArrival from '../components/PageArrival';
import { FileText as A1, Download as A2, Users as A3, Sparkles as A4, Target as A5, Wallet as A6 } from 'lucide-react';

import { useNavigate } from 'react-router-dom';
import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { 
  FileText, Download, Mail, Link2, Check, Loader2,
  GraduationCap, DollarSign, Calendar, Target, Brain, MapPin, BookOpen,
  Heart, Briefcase, Award, Users, CheckSquare, Square, Rocket, 
  TrendingUp, Sparkles, Shield, Clock, ArrowRight, Star, ChevronLeft,
  Eye, Trash2, History, Zap, AlertCircle, Calculator, BadgeCheck
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import jsPDF from 'jspdf';
import { motion } from 'framer-motion';
import { generateReportPDF } from '../utils/reportPdf';
import { ReportPreviewModal } from '../components/ReportPreviewModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReportPage() {
  const [arrived, setArrived] = useState(false);
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [allData, setAllData] = useState({});
  const [goals, setGoals] = useState([]);
  const [favoriteColleges, setFavoriteColleges] = useState([]);
  const [children, setChildren] = useState([]);
  const [history, setHistory] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewKeys, setPreviewKeys] = useState([]);
  
  const isParent = user?.role === 'parent';
  
  // Selectable report sections - different for parent vs student
  const [selectedSections, setSelectedSections] = useState(
    isParent 
      ? {
          family_overview: true,
          children_summary: true,
          financial_overview: true,
          scholarships: true,
          milestones: true,
        }
      : {
          profile: true,
          personality: true,
          launch_readiness: true,
          brand_network: true,
          radar: true,
          future_self: true,
          learning_roi: true,
          proof: true,
          life_design: true,
          financial_plan: true,
          future_outlook: true,
          careers: true,
          pathways: true,
          scholarships: true,
          colleges: true,
          favoriteColleges: true,
          experiences: true,
          skills: true,
          financial_literacy: true,
          decision_lab: true,
          goals: true,
          action_plan: true,
          roadmap_90day: true,
          roadmap_12month: true,
        }
  );

  useEffect(() => {
    if (isParent) {
      fetchParentData();
    } else {
      fetchStudentData();
    }
  }, [isParent]);

  const fetchStudentData = async () => {
    try {
      const [dataRes, goalsRes, favoritesRes, calcRes, assessRes, intelRes] = await Promise.all([
        axios.get(`${API}/user-data`, { ...getAuthHeader(), withCredentials: true }),
        axios.get(`${API}/goals`, { ...getAuthHeader(), withCredentials: true }),
        axios.get(`${API}/colleges/favorites`, getAuthHeader()).catch(() => ({ data: { favorites: [] } })),
        axios.get(`${API}/calculator/results`, { ...getAuthHeader(), withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API}/assessment/results`, getAuthHeader()).catch(() => ({ data: { started: false } })),
        axios.get(`${API}/futurepath-report/full-intelligence`, getAuthHeader()).catch(() => ({ data: null }))
      ]);

      setAllData({
        ...dataRes.data,
        calculator: calcRes.data?.success ? calcRes.data.data : null,
        assessment: assessRes.data?.started ? assessRes.data : null,
        intel: intelRes.data || null,
      });
      setGoals(goalsRes.data.goals || []);
      setFavoriteColleges(favoritesRes.data.favorites || []);
      fetchHistory();
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/futurepath-report/history`, { ...getAuthHeader(), withCredentials: true });
      setHistory(res.data.history || []);
    } catch (error) {
      // non-blocking
    }
  };

  const fetchParentData = async () => {
    try {
      const [childrenRes] = await Promise.all([
        axios.get(`${API}/family/children`, { ...getAuthHeader(), withCredentials: true }).catch(() => ({ data: { children: [] } }))
      ]);
      
      setChildren(childrenRes.data.children || []);
    } catch (error) {
      console.error('Failed to fetch parent data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (key) => {
    setSelectedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const selectAll = () => {
    const allSelected = Object.keys(selectedSections).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setSelectedSections(allSelected);
  };

  const deselectAll = () => {
    const allDeselected = Object.keys(selectedSections).reduce((acc, key) => {
      acc[key] = false;
      return acc;
    }, {});
    setSelectedSections(allDeselected);
  };

  const allSelected = Object.values(selectedSections).every(v => v);
  const noneSelected = Object.values(selectedSections).every(v => !v);

  // Generate PDF for Parents
  const generateParentPDF = async () => {
    setGenerating(true);
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const maxTextWidth = pageWidth - 2 * margin;
      let yPos = margin;

      const addNewPage = () => {
        pdf.addPage();
        yPos = margin;
      };

      const checkPageBreak = (height) => {
        if (yPos + height > pageHeight - margin) {
          addNewPage();
        }
      };

      const addSectionHeader = (title, color) => {
        checkPageBreak(15);
        pdf.setFillColor(...color);
        pdf.rect(margin - 5, yPos - 5, maxTextWidth + 10, 10, 'F');
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(255, 255, 255);
        pdf.text(title, margin, yPos + 2);
        yPos += 15;
        pdf.setTextColor(30, 41, 59);
      };

      // ===== COVER PAGE - Clean Dark =====
      // Dark background
      pdf.setFillColor(15, 23, 42); // slate-900
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');
      
      // Add logo image
      try {
        const logoImg = new Image();
        logoImg.src = '/fp-logo.png';
        await new Promise((resolve) => { logoImg.onload = resolve; logoImg.onerror = resolve; });
        if (logoImg.complete && logoImg.naturalWidth > 0) {
          pdf.addImage(logoImg, 'PNG', pageWidth/2 - 25, 30, 50, 50);
        }
      } catch {
        // Fallback: text logo
        pdf.setFillColor(37, 99, 235);
        pdf.roundedRect(pageWidth/2 - 20, 35, 40, 40, 8, 8, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(24);
        pdf.setFont('helvetica', 'bold');
        pdf.text('FP', pageWidth/2, 62, { align: 'center' });
      }
      
      // Title
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(42);
      pdf.setFont('helvetica', 'bold');
      pdf.text('AGENTIC FUTUREPATH AI', pageWidth/2, 100, { align: 'center' });
      
      // Subtitle
      pdf.setFontSize(28);
      pdf.setFont('helvetica', 'normal');
      pdf.text('My FuturePath Report', pageWidth/2, 118, { align: 'center' });
      
      // Decorative line
      pdf.setDrawColor(96, 165, 250); // blue-400
      pdf.setLineWidth(1);
      pdf.line(pageWidth/2 - 50, 128, pageWidth/2 + 50, 128);
      
      // Prepared for
      pdf.setFontSize(14);
      pdf.setTextColor(148, 163, 184); // slate-400
      pdf.text('Prepared for', pageWidth/2, 150, { align: 'center' });
      
      pdf.setFontSize(22);
      pdf.setTextColor(255, 255, 255);
      pdf.setFont('helvetica', 'bold');
      pdf.text(user?.full_name || 'Parent', pageWidth/2, 165, { align: 'center' });
      
      // Date
      pdf.setFontSize(12);
      pdf.setTextColor(148, 163, 184);
      pdf.setFont('helvetica', 'normal');
      pdf.text(new Date().toLocaleDateString('en-US', { 
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
      }), pageWidth/2, 180, { align: 'center' });

      // ===== REPORT SUMMARY PAGE =====
      addNewPage();
      pdf.setFillColor(30, 41, 59); // slate-800
      pdf.rect(0, 0, pageWidth, 50, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('About This Report', pageWidth/2, 32, { align: 'center' });
      
      yPos = 65;
      pdf.setTextColor(30, 41, 59);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      
      const summaryText = `This comprehensive Family Report from Agentic FuturePath AI provides you with a complete overview of your family's educational journey and career planning progress. As a parent, this report helps you stay informed about your children's pathway choices, financial planning status, scholarship opportunities, and upcoming milestones.

The report is designed to facilitate meaningful conversations between parents and children about future planning, while also serving as a valuable reference when meeting with school counselors, financial advisors, or other educational professionals.`;
      
      const summaryLines = pdf.splitTextToSize(summaryText, maxTextWidth);
      pdf.text(summaryLines, margin, yPos);
      yPos += summaryLines.length * 5 + 15;

      // Table of Contents
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(37, 99, 235);
      pdf.text('Table of Contents', margin, yPos);
      yPos += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(30, 41, 59);
      
      const tocItems = [
        { section: 'Family Overview', desc: 'Your family information and linked children' },
        { section: 'Children Summary', desc: 'Overview of each child\'s educational journey' },
        { section: 'Financial Planning', desc: 'Cost estimates, savings, and funding strategies' },
        { section: 'Scholarship Tracking', desc: 'Scholarship opportunities and applications' },
        { section: 'Upcoming Milestones', desc: 'Important dates and deadlines to remember' },
      ];
      
      tocItems.forEach((item, index) => {
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${index + 1}. ${item.section}`, margin, yPos);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 116, 139);
        pdf.text(item.desc, margin + 5, yPos + 5);
        pdf.setTextColor(30, 41, 59);
        yPos += 14;
      });
      
      yPos += 10;

      // Key Takeaways Box
      pdf.setFillColor(240, 249, 255); // blue-50
      pdf.roundedRect(margin - 5, yPos - 5, maxTextWidth + 10, 55, 3, 3, 'F');
      
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 64, 175);
      pdf.text('Key Takeaways', margin, yPos + 5);
      yPos += 15;
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(30, 41, 59);
      
      const keyTakeaways = [
        `• ${children.length} children linked to your family account`,
        '• Review pathway choices and provide feedback through the Family Decision Workflow',
        '• Track scholarship deadlines and application status',
        '• Stay informed about upcoming milestones and important dates',
      ];
      
      keyTakeaways.forEach(takeaway => {
        const takeawayLines = pdf.splitTextToSize(takeaway, maxTextWidth - 5);
        pdf.text(takeawayLines, margin, yPos);
        yPos += takeawayLines.length * 4 + 2;
      });

      // ===== FAMILY OVERVIEW =====
      if (selectedSections.family_overview) {
        addNewPage();
        addSectionHeader('Family Overview', [236, 72, 153]);
        
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        
        const familyInfo = [
          ['Parent Name', user?.full_name || 'N/A'],
          ['Email', user?.email || 'N/A'],
          ['Children', `${children.length} linked`],
          ['Report Date', new Date().toLocaleDateString()]
        ];
        
        familyInfo.forEach(([label, value]) => {
          checkPageBreak(8);
          pdf.setFont('helvetica', 'bold');
          pdf.text(`${label}: `, margin, yPos);
          pdf.setFont('helvetica', 'normal');
          const valueLines = pdf.splitTextToSize(String(value), maxTextWidth - 50);
          pdf.text(valueLines, margin + 45, yPos);
          yPos += valueLines.length * 5 + 2;
        });
        yPos += 10;
      }

      // ===== CHILDREN SUMMARY =====
      if (selectedSections.children_summary && children.length > 0) {
        addSectionHeader('Children Summary', [59, 130, 246]);
        
        children.forEach((child, index) => {
          checkPageBreak(40);
          
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          pdf.text(`${index + 1}. ${child.full_name || 'Child'}`, margin, yPos);
          yPos += 6;
          
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
          
          const childInfo = [
            child.learning_stage ? `Stage: ${child.learning_stage.replace('_', ' ')}` : null,
            child.focused_path_name ? `Selected Path: ${child.focused_path_name}` : 'Path: Exploring',
            child.pathway_preference ? `Preference: ${child.pathway_preference}` : null,
          ].filter(Boolean);
          
          childInfo.forEach(info => {
            const infoLines = pdf.splitTextToSize(info, maxTextWidth - 10);
            pdf.text(infoLines, margin + 5, yPos);
            yPos += infoLines.length * 4 + 2;
          });
          
          yPos += 8;
        });
      }

      // ===== FINANCIAL OVERVIEW =====
      if (selectedSections.financial_overview) {
        addSectionHeader('Financial Planning Overview', [16, 185, 129]);
        
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        
        const financialNotes = [
          '• Review estimated costs for each child\'s chosen pathway',
          '• Track scholarship applications and awards',
          '• Monitor 529 savings plan contributions',
          '• Plan for funding gaps and loan options'
        ];
        
        financialNotes.forEach(note => {
          checkPageBreak(8);
          const noteLines = pdf.splitTextToSize(note, maxTextWidth - 5);
          pdf.text(noteLines, margin, yPos);
          yPos += noteLines.length * 4 + 2;
        });
        
        yPos += 10;
      }

      // ===== MILESTONES =====
      if (selectedSections.milestones) {
        addSectionHeader('Upcoming Milestones', [245, 158, 11]);
        
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        
        const milestones = [
          '• Application deadlines for colleges/programs',
          '• Scholarship submission dates',
          '• Test registration dates (SAT, ACT, etc.)',
          '• Campus visit scheduling',
          '• Financial aid application deadlines'
        ];
        
        milestones.forEach(milestone => {
          checkPageBreak(8);
          const milestoneLines = pdf.splitTextToSize(milestone, maxTextWidth - 5);
          pdf.text(milestoneLines, margin, yPos);
          yPos += milestoneLines.length * 4 + 2;
        });
      }

      // ===== FOOTER =====
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(148, 163, 184);
        if (i > 1) {
          pdf.text(
            `Page ${i - 1} of ${totalPages - 1} | Agentic FuturePath AI - My FuturePath Report`,
            pageWidth / 2,
            pageHeight - 10,
            { align: 'center' }
          );
        }
      }

      pdf.save(`FuturePath_Family_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('Family report downloaded!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate report. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  // Build the generator section keys from the current checkbox selection.
  const buildReportKeys = () => {
    const map = {
      personality: 'personality', financial_plan: 'lifestyle', pathways: 'pathways',
      scholarships: 'scholarships', colleges: 'colleges', favoriteColleges: 'colleges', goals: 'goals',
      future_outlook: 'outlook', careers: 'careers', experiences: 'experiences',
      skills: 'skills', financial_literacy: 'financial_literacy', decision_lab: 'decision_lab',
    };
    const keys = new Set();
    Object.entries(selectedSections).forEach(([k, v]) => { if (v && map[k]) keys.add(map[k]); });
    return [...keys];
  };

  const fetchReportData = async () => {
    const res = await axios.get(`${API}/futurepath-report/comprehensive?narrative=1`, { ...getAuthHeader(), withCredentials: true });
    return res.data;
  };

  const saveReportHistory = async (data, keys) => {
    try {
      const stats = { sectionCount: keys.length, name: data?.profile?.name || user?.full_name || 'Student' };
      await axios.post(`${API}/futurepath-report/history`, { sections: keys, stats, data }, { ...getAuthHeader(), withCredentials: true });
      fetchHistory();
    } catch (error) {
      // history is a nice-to-have; never block the download
    }
  };

  // Open the live in-app preview (fetches the aggregated report, then renders pages).
  const openPreview = async () => {
    if (noneSelected) {
      toast.error('Please select at least one section to include in the report');
      return;
    }
    setGenerating(true);
    try {
      const data = await fetchReportData();
      setPreviewData(data);
      setPreviewKeys(buildReportKeys());
      setPreviewOpen(true);
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to build preview. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  // Re-open a past report from history (uses the stored snapshot, no refetch).
  const previewHistoryItem = async (id) => {
    try {
      const res = await axios.get(`${API}/futurepath-report/history/${id}`, { ...getAuthHeader(), withCredentials: true });
      const rec = res.data.report;
      setPreviewData(rec.data);
      setPreviewKeys(rec.sections || []);
      setPreviewOpen(true);
    } catch (error) {
      toast.error('Could not open this report');
    }
  };

  const deleteHistoryItem = async (id) => {
    try {
      await axios.delete(`${API}/futurepath-report/history/${id}`, { ...getAuthHeader(), withCredentials: true });
      setHistory((h) => h.filter((x) => x.id !== id));
      toast.success('Report removed from history');
    } catch (error) {
      toast.error('Failed to remove report');
    }
  };

  const handlePreviewDownloaded = () => {
    celebrate('Your FuturePath report is ready. Go show somebody.');
    if (previewData) saveReportHistory(previewData, previewKeys);
  };

  // Generate PDF for Students, uses the new typeset generator (cover + TOC +
  // narrative + all modules). Checkboxes drive which sections are included.
  const generateStudentPDF = async () => {
    if (noneSelected) {
      toast.error('Please select at least one section to include in the report');
      return;
    }
    setGenerating(true);
    try {
      const data = await fetchReportData();
      const keys = buildReportKeys();
      await generateReportPDF(data, keys);
      await saveReportHistory(data, keys);
      toast.success('Comprehensive report downloaded!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate report. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  // Legacy inline student PDF (kept for reference, no longer used)
  // eslint-disable-next-line no-unused-vars
  const _generateStudentPDF_legacy = async () => {
    if (noneSelected) {
      toast.error('Please select at least one section to include in the report');
      return;
    }

    setGenerating(true);
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const maxTextWidth = pageWidth - 2 * margin;
      let yPos = margin;

      const addNewPage = () => {
        pdf.addPage();
        yPos = margin;
      };

      const checkPageBreak = (height) => {
        if (yPos + height > pageHeight - margin) {
          addNewPage();
        }
      };

      const addSectionHeader = (title, color) => {
        checkPageBreak(15);
        pdf.setFillColor(...color);
        pdf.rect(margin - 5, yPos - 5, maxTextWidth + 10, 10, 'F');
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(255, 255, 255);
        pdf.text(title, margin, yPos + 2);
        yPos += 15;
        pdf.setTextColor(30, 41, 59);
      };

      // Helper to wrap text
      const addWrappedText = (text, x, maxWidth) => {
        const lines = pdf.splitTextToSize(String(text || ''), maxWidth);
        pdf.text(lines, x, yPos);
        return lines.length;
      };

      // ===== COVER PAGE - Blue Gradient like Homepage =====
      // Deep blue background
      pdf.setFillColor(15, 23, 42); // slate-900
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');
      
      // Add gradient overlay circles (simulating the homepage look)
      pdf.setFillColor(30, 64, 175); // blue-800
      pdf.circle(pageWidth * 0.3, pageHeight * 0.2, 60, 'F');
      pdf.setFillColor(88, 28, 135); // purple-900
      pdf.circle(pageWidth * 0.7, pageHeight * 0.7, 70, 'F');
      pdf.setFillColor(6, 182, 212); // cyan-500
      pdf.circle(pageWidth * 0.8, pageHeight * 0.3, 40, 'F');
      
      // Logo area - FP initials
      pdf.setFillColor(37, 99, 235); // blue-600
      pdf.roundedRect(pageWidth/2 - 20, 35, 40, 40, 8, 8, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text('FP', pageWidth/2, 62, { align: 'center' });
      
      // Title
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(42);
      pdf.setFont('helvetica', 'bold');
      pdf.text('AGENTIC FUTUREPATH AI', pageWidth/2, 100, { align: 'center' });
      
      // Subtitle
      pdf.setFontSize(28);
      pdf.setFont('helvetica', 'normal');
      pdf.text('My FuturePath Report', pageWidth/2, 118, { align: 'center' });
      
      // Decorative line
      pdf.setDrawColor(96, 165, 250); // blue-400
      pdf.setLineWidth(1);
      pdf.line(pageWidth/2 - 50, 128, pageWidth/2 + 50, 128);
      
      // Prepared for
      pdf.setFontSize(14);
      pdf.setTextColor(148, 163, 184); // slate-400
      pdf.text('Prepared for', pageWidth/2, 150, { align: 'center' });
      
      pdf.setFontSize(22);
      pdf.setTextColor(255, 255, 255);
      pdf.setFont('helvetica', 'bold');
      pdf.text(user?.full_name || 'Student', pageWidth/2, 165, { align: 'center' });
      
      // Education info
      if (user?.education_level || user?.learning_stage) {
        pdf.setFontSize(12);
        pdf.setTextColor(96, 165, 250); // blue-400
        pdf.setFont('helvetica', 'normal');
        const eduText = user?.learning_stage?.replace('_', ' ') || user?.education_level?.replace('_', ' ') || '';
        pdf.text(eduText.charAt(0).toUpperCase() + eduText.slice(1), pageWidth/2, 178, { align: 'center' });
      }
      
      // Date
      pdf.setFontSize(12);
      pdf.setTextColor(148, 163, 184);
      pdf.setFont('helvetica', 'normal');
      pdf.text(new Date().toLocaleDateString('en-US', { 
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
      }), pageWidth/2, 195, { align: 'center' });

      // ===== REPORT SUMMARY PAGE =====
      addNewPage();
      pdf.setFillColor(30, 41, 59); // slate-800
      pdf.rect(0, 0, pageWidth, 50, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('About This Report', pageWidth/2, 32, { align: 'center' });
      
      yPos = 65;
      pdf.setTextColor(30, 41, 59);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      
      const summaryText = `This comprehensive Career Report from Agentic FuturePath AI provides you with a personalized overview of your educational and career planning journey. The report includes your unique personality profile, recommended career pathways, scholarship opportunities, and strategic action plans.

Use this report to track your progress, share with counselors or parents, and stay focused on your goals. Each section is designed to help you make informed decisions about your future.`;
      
      const summaryLines = pdf.splitTextToSize(summaryText, maxTextWidth);
      pdf.text(summaryLines, margin, yPos);
      yPos += summaryLines.length * 5 + 15;

      // Table of Contents
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(37, 99, 235);
      pdf.text('Table of Contents', margin, yPos);
      yPos += 10;
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(30, 41, 59);
      
      const tocItems = [
        { section: 'Student Profile', desc: 'Your personal information and interests', included: selectedSections.profile },
        { section: 'Personality Profile', desc: 'Your unique traits and learning style', included: selectedSections.personality },
        { section: 'Career Pathways', desc: 'Top 3 AI-recommended career paths', included: selectedSections.pathways },
        { section: 'Scholarships', desc: 'Matched scholarship opportunities', included: selectedSections.scholarships },
        { section: 'College Recommendations', desc: 'Best-fit colleges for your goals', included: selectedSections.colleges },
        { section: 'Favorite Colleges', desc: 'Your saved college choices', included: selectedSections.favoriteColleges },
        { section: 'My Goals', desc: 'Your goal tracking summary', included: selectedSections.goals },
        { section: '7-Day Action Plan', desc: 'This week\'s action items', included: selectedSections.action_plan },
        { section: '90-Day Roadmap', desc: 'Quarterly strategic milestones', included: selectedSections.roadmap_90day },
        { section: '12-Month Strategic Plan', desc: 'Year-long career roadmap', included: selectedSections.roadmap_12month },
      ];
      
      let tocNum = 1;
      tocItems.forEach((item) => {
        if (item.included) {
          pdf.setFont('helvetica', 'bold');
          pdf.text(`${tocNum}. ${item.section}`, margin, yPos);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(100, 116, 139);
          pdf.text(item.desc, margin + 5, yPos + 4);
          pdf.setTextColor(30, 41, 59);
          yPos += 12;
          tocNum++;
        }
      });
      
      yPos += 8;

      // Key Takeaways Box
      pdf.setFillColor(240, 249, 255); // blue-50
      const takeawayBoxHeight = 50;
      pdf.roundedRect(margin - 5, yPos - 5, maxTextWidth + 10, takeawayBoxHeight, 3, 3, 'F');
      
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 64, 175);
      pdf.text('Key Takeaways', margin, yPos + 5);
      yPos += 15;
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(30, 41, 59);
      
      const pathwaysData = allData.pathways?.data;
      const scholarshipsData = allData.scholarships?.data;
      
      const keyTakeaways = [
        pathwaysData?.paths?.[0] ? `• Top Career Match: ${pathwaysData.paths[0].title} (${pathwaysData.paths[0].match_score}% match)` : '• Complete your profile to get personalized pathway recommendations',
        scholarshipsData?.total_potential ? `• Total Scholarship Potential: ${scholarshipsData.total_potential}` : '• Generate scholarship matches to find funding opportunities',
        `• ${goals.length} goals tracked in your Kanban board`,
      ];
      
      keyTakeaways.forEach(takeaway => {
        const takeawayLines = pdf.splitTextToSize(takeaway, maxTextWidth - 5);
        pdf.text(takeawayLines, margin, yPos);
        yPos += takeawayLines.length * 4 + 2;
      });
      
      yPos += 15;

      // ===== STUDENT PROFILE =====
      if (selectedSections.profile) {
        addNewPage();
        addSectionHeader('Student Profile', [30, 58, 138]);
        
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        const profileFields = [
          ['Name', user?.full_name || 'N/A'],
          ['Education Level', user?.education_level?.replace('_', ' ') || 'N/A'],
          ['Location', `${user?.city || 'N/A'}, ${user?.state || ''}`],
          ['Interests', (user?.interests || []).slice(0, 5).join(', ') || 'N/A'],
          ['Career Goals', user?.career_aspirations || 'Exploring options'],
          ['Pathway Preference', user?.pathway_preference?.replace('_', ' ') || 'Undecided']
        ];
        
        profileFields.forEach(([label, value]) => {
          checkPageBreak(12);
          pdf.setFont('helvetica', 'bold');
          pdf.text(`${label}: `, margin, yPos);
          pdf.setFont('helvetica', 'normal');
          const valueLines = pdf.splitTextToSize(String(value), maxTextWidth - 50);
          pdf.text(valueLines, margin + 45, yPos);
          yPos += valueLines.length * 5 + 2;
        });
        
        if (user?.bio) {
          yPos += 5;
          pdf.setFont('helvetica', 'bold');
          pdf.text('Bio:', margin, yPos);
          yPos += 6;
          pdf.setFont('helvetica', 'normal');
          const bioLines = pdf.splitTextToSize(user.bio, maxTextWidth);
          pdf.text(bioLines, margin, yPos);
          yPos += bioLines.length * 4 + 5;
        }
      }

      // ===== PERSONALITY PROFILE =====
      if (selectedSections.personality) {
        const personality = allData.personality?.data;
        if (personality) {
          yPos += 10;
          addSectionHeader('Personality Profile', [126, 34, 206]);
          
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          const typeName = personality.personality_type?.name || 'Your Personality Type';
          const typeLines = pdf.splitTextToSize(typeName, maxTextWidth);
          pdf.text(typeLines, margin, yPos);
          yPos += typeLines.length * 5 + 3;
          
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
          if (personality.personality_type?.description) {
            const descLines = pdf.splitTextToSize(personality.personality_type.description, maxTextWidth);
            pdf.text(descLines, margin, yPos);
            yPos += descLines.length * 4 + 5;
          }
          
          if (personality.personality_type?.strengths?.length > 0) {
            pdf.setFont('helvetica', 'bold');
            pdf.text('Strengths:', margin, yPos);
            yPos += 5;
            pdf.setFont('helvetica', 'normal');
            const strengthsText = personality.personality_type.strengths.slice(0, 4).join(', ');
            const strengthLines = pdf.splitTextToSize(strengthsText, maxTextWidth - 5);
            pdf.text(strengthLines, margin + 5, yPos);
            yPos += strengthLines.length * 4 + 3;
          }
          
          if (personality.personality_type?.growth_areas?.length > 0) {
            pdf.setFont('helvetica', 'bold');
            pdf.text('Growth Areas:', margin, yPos);
            yPos += 5;
            pdf.setFont('helvetica', 'normal');
            const growthText = personality.personality_type.growth_areas.slice(0, 4).join(', ');
            const growthLines = pdf.splitTextToSize(growthText, maxTextWidth - 5);
            pdf.text(growthLines, margin + 5, yPos);
            yPos += growthLines.length * 4 + 3;
          }
        }
      }

      // ===== SCORED ASSESSMENT (RIASEC + Big Five + environment + values) =====
      if (selectedSections.personality && allData.assessment) {
        const a = allData.assessment;
        yPos += 10;
        addSectionHeader('Assessment Results', [126, 34, 206]);

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`Holland Code: ${a.holland?.code || '-'}${a.headline_type ? `  ·  ${a.headline_type.name}` : ''}`, margin, yPos);
        yPos += 6;
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        const conf = pdf.splitTextToSize(
          `Scored from ${a.answered} of ${a.of} answers. Interests: ${a.holland?.confidence?.level}. ` +
          `Traits: ${a.traits?.confidence?.level}. Sources: O*NET RIASEC, Big Five, O*NET Work Context and Work Values.`,
          maxTextWidth);
        pdf.text(conf, margin, yPos);
        yPos += conf.length * 4 + 4;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Interests (RIASEC)', margin, yPos); yPos += 5;
        pdf.setFont('helvetica', 'normal');
        (a.holland?.scores || []).forEach(sc => {
          pdf.text(`${sc.label}: ${sc.score}/100, ${sc.blurb}`, margin + 5, yPos);
          yPos += 4.5;
        });
        yPos += 3;

        if ((a.traits?.rows || []).length) {
          pdf.setFont('helvetica', 'bold');
          pdf.text('Traits (Big Five)', margin, yPos); yPos += 5;
          pdf.setFont('helvetica', 'normal');
          a.traits.rows.forEach(t => {
            const line = pdf.splitTextToSize(`${t.label}: ${t.score}/100, ${t.so_what}`, maxTextWidth - 5);
            pdf.text(line, margin + 5, yPos);
            yPos += line.length * 4 + 1;
          });
          yPos += 3;
        }

        if ((a.environment?.rows || []).length) {
          pdf.setFont('helvetica', 'bold');
          pdf.text('Work environment fit', margin, yPos); yPos += 5;
          pdf.setFont('helvetica', 'normal');
          const env = pdf.splitTextToSize(a.environment.rows.map(e => e.reads_as).join(' · '), maxTextWidth - 5);
          pdf.text(env, margin + 5, yPos); yPos += env.length * 4 + 4;
        }

        if ((a.values?.rows || []).length) {
          pdf.setFont('helvetica', 'bold');
          pdf.text('What the work has to give you', margin, yPos); yPos += 5;
          pdf.setFont('helvetica', 'normal');
          const vals = pdf.splitTextToSize(a.values.rows.map(v => `${v.label} ${v.score}`).join(' · '), maxTextWidth - 5);
          pdf.text(vals, margin + 5, yPos); yPos += vals.length * 4 + 4;
        }

        if ((a.strengths || []).length) {
          pdf.setFont('helvetica', 'bold');
          pdf.text('Strengths', margin, yPos); yPos += 5;
          pdf.setFont('helvetica', 'normal');
          a.strengths.slice(0, 5).forEach(x => {
            const l = pdf.splitTextToSize(`• ${x}`, maxTextWidth - 5);
            pdf.text(l, margin + 5, yPos); yPos += l.length * 4;
          });
          yPos += 3;
        }

        if ((a.derailers || []).length) {
          pdf.setFont('helvetica', 'bold');
          pdf.text('What is likely to trip you up', margin, yPos); yPos += 5;
          pdf.setFont('helvetica', 'normal');
          a.derailers.slice(0, 4).forEach(x => {
            const l = pdf.splitTextToSize(`• ${x}`, maxTextWidth - 5);
            pdf.text(l, margin + 5, yPos); yPos += l.length * 4;
          });
          yPos += 3;
        }

        if ((a.career_matches || []).length) {
          pdf.setFont('helvetica', 'bold');
          pdf.text('Careers that serve this code', margin, yPos); yPos += 5;
          pdf.setFont('helvetica', 'normal');
          const m = pdf.splitTextToSize(
            a.career_matches.slice(0, 9).map(x => `${x.title} (${x.field})`).join(' · '), maxTextWidth - 5);
          pdf.text(m, margin + 5, yPos); yPos += m.length * 4 + 3;
        }

        pdf.setFontSize(8);
        const bound = pdf.splitTextToSize(
          'This assessment measures interests, traits, environment fit and values. It does not measure aptitude, ' +
          'and it should never be the only reason to choose or rule out a career.', maxTextWidth);
        pdf.text(bound, margin, yPos); yPos += bound.length * 3.5 + 4;
        pdf.setFontSize(10);
      }

      // ===== COST OF THE LIFE THEY WANT (deep cost model) =====
      if (selectedSections.life_design && allData.calculator?.cost_model) {
        const cm = allData.calculator.cost_model;
        const sal = allData.calculator.salary || {};
        yPos += 10;
        addSectionHeader('The Cost Of The Life You Want', [8, 145, 178]);
        pdf.setFontSize(10); pdf.setFont('helvetica', 'bold');
        pdf.text(`Salary required: ${sal.required_salary ? '$' + Number(sal.required_salary).toLocaleString() : 'not set'}` +
          `${sal.monthly_total ? `   ·   $${Number(sal.monthly_total).toLocaleString()} per month all in` : ''}`, margin, yPos);
        yPos += 6; pdf.setFontSize(9); pdf.setFont('helvetica', 'normal');
        pdf.text(`Confidence: ${cm.confidence} (based on how much of the nine-step model was answered)`, margin, yPos);
        yPos += 5;
        if (cm.hourly_equivalent) {
          pdf.text(`That is $${cm.hourly_equivalent}/hour before tax, at ${cm.effective_tax_rate || 0}% effective tax.`, margin, yPos);
          yPos += 5;
        }
        (cm.per_step || []).filter(p => p.monthly > 0).forEach(p => {
          pdf.text(`${p.label}: $${Number(p.monthly).toLocaleString()}/month (${p.answered}/${p.of} answered)`, margin + 5, yPos);
          yPos += 4.5;
        });
        if ((cm.blind_spots || []).length) {
          yPos += 3; pdf.setFont('helvetica', 'bold');
          pdf.text('Costs still unaccounted for', margin, yPos); yPos += 5;
          pdf.setFont('helvetica', 'normal');
          cm.blind_spots.forEach(b => {
            const l = pdf.splitTextToSize(`• ${b.note}`, maxTextWidth - 5);
            pdf.text(l, margin + 5, yPos); yPos += l.length * 4;
          });
        }
        if ((cm.levers || []).length) {
          yPos += 3; pdf.setFont('helvetica', 'bold');
          pdf.text('How to reach this sooner', margin, yPos); yPos += 5;
          pdf.setFont('helvetica', 'normal');
          cm.levers.forEach(l => {
            const t = pdf.splitTextToSize(`• ${l.action}, saves about $${Number(l.saves).toLocaleString()}/month. ${l.note || ''}`, maxTextWidth - 5);
            pdf.text(t, margin + 5, yPos); yPos += t.length * 4;
          });
        }
        if ((cm.work_tensions || []).length) {
          yPos += 3; pdf.setFont('helvetica', 'bold');
          pdf.text('What they want from the work, against what the assessment measured', margin, yPos); yPos += 5;
          pdf.setFont('helvetica', 'normal');
          cm.work_tensions.forEach(t => {
            const l = pdf.splitTextToSize(`• ${t.note}`, maxTextWidth - 5);
            pdf.text(l, margin + 5, yPos); yPos += l.length * 4;
          });
        }
        yPos += 4;
      }

      // ===== LIFE DESIGN: WHO YOU ARE vs THE NUMBERS =====
      if (selectedSections.life_design && allData.intel?.life_design) {
        const ld = allData.intel.life_design;
        yPos += 10;
        addSectionHeader('The Life You Want vs The Work That Fits', [124, 58, 237]);
        pdf.setFontSize(9); pdf.setFont('helvetica', 'normal');
        pdf.text(`Income floor: ${ld.income_floor ? '$' + ld.income_floor.toLocaleString() : 'not set'}` +
          `${ld.assessment ? `   ·   Interest code: ${ld.assessment.code} (${ld.assessment.headline || ''})` : ''}`, margin, yPos);
        yPos += 5;
        if (ld.target_verdict) {
          const v = ld.target_verdict;
          pdf.setFont('helvetica', 'bold');
          pdf.text(`${v.career}${v.median ? `, median $${v.median.toLocaleString()}` : ''}${v.fit_band ? `, ${v.fit_band} interest fit` : ''}`, margin, yPos);
          yPos += 5; pdf.setFont('helvetica', 'normal');
          const h = pdf.splitTextToSize(v.headline || '', maxTextWidth); pdf.text(h, margin, yPos); yPos += h.length * 4 + 2;
        }
        if ((ld.fit_and_affordable || []).length) {
          pdf.setFont('helvetica', 'bold'); pdf.text('Fits you and pays for the life you described', margin, yPos); yPos += 5;
          pdf.setFont('helvetica', 'normal');
          ld.fit_and_affordable.slice(0, 9).forEach(c => {
            pdf.text(`• ${c.title} (${c.field}), $${(c.median || 0).toLocaleString()}` +
              `${c.clears_floor === true ? ' · clears your floor' : c.clears_floor === false ? ' · under your floor' : ''}` +
              `${c.fit ? ` · ${c.fit} fit` : ''}`, margin + 5, yPos);
            yPos += 4;
          });
          yPos += 2;
        }
        if ((ld.environment_conflicts || []).length) {
          pdf.setFont('helvetica', 'bold'); pdf.text('Where you would be unhappy regardless of pay', margin, yPos); yPos += 5;
          pdf.setFont('helvetica', 'normal');
          ld.environment_conflicts.forEach(c => {
            const l = pdf.splitTextToSize(`• ${c.note}`, maxTextWidth - 5); pdf.text(l, margin + 5, yPos); yPos += l.length * 4;
          });
          yPos += 2;
        }
        if ((ld.roadmap_seeds || []).length) {
          pdf.setFont('helvetica', 'bold'); pdf.text('What this makes your next step', margin, yPos); yPos += 5;
          pdf.setFont('helvetica', 'normal');
          ld.roadmap_seeds.forEach(sd => {
            const l = pdf.splitTextToSize(`• ${sd.action}, ${sd.why}`, maxTextWidth - 5);
            pdf.text(l, margin + 5, yPos); yPos += l.length * 4;
          });
        }
        yPos += 4;
      }

      // ===== CAREER LAUNCH READINESS =====
      if (selectedSections.launch_readiness && allData.intel?.readiness) {
        const rd = allData.intel.readiness;
        yPos += 10;
        addSectionHeader('Career Launch Readiness', [8, 145, 178]);
        pdf.setFontSize(11); pdf.setFont('helvetica', 'bold');
        pdf.text(`Overall: ${rd.overall}/100 (${rd.band})${rd.target ? `  ·  measured against ${rd.target}` : ''}`, margin, yPos);
        yPos += 6; pdf.setFontSize(9); pdf.setFont('helvetica', 'normal');
        (rd.components || []).forEach(c => { pdf.text(`${c.label}: ${c.score}/100`, margin + 5, yPos); yPos += 4.5; });
        yPos += 2;
        if ((rd.top_actions || []).length) {
          pdf.setFont('helvetica', 'bold'); pdf.text('What moves this score most', margin, yPos); yPos += 5;
          pdf.setFont('helvetica', 'normal');
          rd.top_actions.forEach(a => {
            const l = pdf.splitTextToSize(`• ${a.action}, ${a.why}`, maxTextWidth - 5);
            pdf.text(l, margin + 5, yPos); yPos += l.length * 4;
          });
        }
        yPos += 4;
      }

      // ===== BRAND, VISIBILITY & NETWORK =====
      if (selectedSections.brand_network && (allData.intel?.positioning || allData.intel?.network)) {
        const po = allData.intel.positioning, nw = allData.intel.network;
        yPos += 6;
        addSectionHeader('Brand, Visibility & Network', [126, 34, 206]);
        pdf.setFontSize(9); pdf.setFont('helvetica', 'normal');
        if (po) {
          pdf.text(`Visibility ${po.visibility_score}/100  ·  Digital professionalism ${po.professionalism_score}/100`, margin, yPos);
          yPos += 5;
          if ((po.pillars || []).length) { pdf.text(`Brand pillars: ${po.pillars.join(' · ')}`, margin, yPos); yPos += 5; }
          if (po.headline) {
            const h = pdf.splitTextToSize(`Headline: ${po.headline}`, maxTextWidth); pdf.text(h, margin, yPos); yPos += h.length * 4 + 2;
          }
          (po.plan || []).slice(0, 4).forEach(st => {
            const l = pdf.splitTextToSize(`• ${st.step} (${st.effort}, raises ${st.raises})`, maxTextWidth - 5);
            pdf.text(l, margin + 5, yPos); yPos += l.length * 4;
          });
          yPos += 3;
        }
        if (nw) {
          pdf.setFont('helvetica', 'bold');
          pdf.text(`Network readiness ${nw.network_readiness}/100, ${nw.roles_filled} of ${nw.roles_total} advisor roles filled`, margin, yPos);
          yPos += 5; pdf.setFont('helvetica', 'normal');
          if (nw.priority_gap) {
            const g = pdf.splitTextToSize(`Close first: ${nw.priority_gap.label}, ${nw.priority_gap.why}`, maxTextWidth);
            pdf.text(g, margin + 5, yPos); yPos += g.length * 4 + 2;
          }
          const filled = (nw.map || []).filter(r => r.filled);
          if (filled.length) {
            const f = pdf.splitTextToSize(`Filled: ${filled.map(r => r.label).join(' · ')}`, maxTextWidth);
            pdf.text(f, margin + 5, yPos); yPos += f.length * 4 + 3;
          }
        }
      }

      // ===== OPPORTUNITY RADAR & RISK =====
      if (selectedSections.radar && allData.intel?.radar) {
        const rr = allData.intel.radar;
        yPos += 6;
        addSectionHeader('Opportunity Radar & Early Warning', [217, 119, 6]);
        pdf.setFontSize(9); pdf.setFont('helvetica', 'bold');
        pdf.text(`Risk band: ${rr.risk?.band} (${rr.risk?.score}/100)`, margin, yPos); yPos += 5;
        pdf.setFont('helvetica', 'normal');
        (rr.risk?.drivers || []).slice(0, 5).forEach(dr => {
          const l = pdf.splitTextToSize(`• ${dr.label} [${dr.severity}], ${dr.detail}`, maxTextWidth - 5);
          pdf.text(l, margin + 5, yPos); yPos += l.length * 4;
        });
        yPos += 3;
        if ((rr.opportunities || []).length) {
          pdf.setFont('helvetica', 'bold'); pdf.text('Opportunities matched to this profile', margin, yPos); yPos += 5;
          pdf.setFont('helvetica', 'normal');
          rr.opportunities.slice(0, 8).forEach(o => {
            const l = pdf.splitTextToSize(
              `• [${o.kind}] ${o.name}${o.org ? `, ${o.org}` : ''}${o.amount ? ` (${o.amount})` : ''}${o.deadline ? ` · deadline ${o.deadline}` : ''}`,
              maxTextWidth - 5);
            pdf.text(l, margin + 5, yPos); yPos += l.length * 4;
          });
          yPos += 3;
        }
        if ((rr.signals || []).length) {
          pdf.setFont('helvetica', 'bold'); pdf.text('Signals to act on', margin, yPos); yPos += 5;
          pdf.setFont('helvetica', 'normal');
          rr.signals.slice(0, 6).forEach(sg => {
            const l = pdf.splitTextToSize(`• ${sg.title} (${sg.urgency}), ${sg.why_now}`, maxTextWidth - 5);
            pdf.text(l, margin + 5, yPos); yPos += l.length * 4;
          });
          yPos += 3;
        }
      }

      // ===== FUTURE SELF SCENARIOS =====
      if (selectedSections.future_self && allData.intel?.future_self) {
        const fs = allData.intel.future_self;
        yPos += 6;
        addSectionHeader(`Your Paths To ${fs.horizon_year}`, [37, 99, 235]);
        pdf.setFontSize(9); pdf.setFont('helvetica', 'normal');
        (fs.paths || []).forEach(pth => {
          checkPageBreak(30);
          pdf.setFont('helvetica', 'bold');
          const t = pdf.splitTextToSize(pth.title, maxTextWidth); pdf.text(t, margin, yPos); yPos += t.length * 4.5;
          pdf.setFont('helvetica', 'normal');
          pdf.text(`${fs.horizon_year} income: ${pth.income_2030 ? '$' + pth.income_2030.toLocaleString() : 'n/a'}  ·  debt: $${(pth.debt_2030 || 0).toLocaleString()}  ·  ${pth.time_cost}`, margin + 5, yPos);
          yPos += 4.5;
          (pth.tradeoffs || []).slice(0, 2).forEach(tr => {
            const l = pdf.splitTextToSize(`- ${tr}`, maxTextWidth - 5); pdf.text(l, margin + 5, yPos); yPos += l.length * 4;
          });
          (pth.must_be_true || []).slice(0, 2).forEach(mb => {
            const l = pdf.splitTextToSize(`Must be true: ${mb}`, maxTextWidth - 5); pdf.text(l, margin + 5, yPos); yPos += l.length * 4;
          });
          yPos += 3;
        });
        pdf.setFontSize(8);
        const dis = pdf.splitTextToSize(fs.disclosure || '', maxTextWidth); pdf.text(dis, margin, yPos);
        yPos += dis.length * 3.5 + 4; pdf.setFontSize(10);
      }

      // ===== LEARNING ROI =====
      if (selectedSections.learning_roi && allData.intel?.learning_roi) {
        const roi = allData.intel.learning_roi;
        yPos += 6;
        addSectionHeader('Learning ROI', [5, 150, 105]);
        pdf.setFontSize(10); pdf.setFont('helvetica', 'bold');
        pdf.text(`${roi.credential?.label}: ${roi.verdict}`, margin, yPos); yPos += 5;
        pdf.setFontSize(9); pdf.setFont('helvetica', 'normal');
        pdf.text(`Total investment $${(roi.investment?.total || 0).toLocaleString()}  ·  gain/yr $${(roi.return?.annual_gain || 0).toLocaleString()}  ·  payback ${roi.return?.payback_years ?? 'n/a'} yrs  ·  10-yr net $${(roi.return?.ten_year_net || 0).toLocaleString()}`, margin, yPos);
        yPos += 5;
        (roi.reasons || []).forEach(r => {
          const l = pdf.splitTextToSize(`• ${r}`, maxTextWidth - 5); pdf.text(l, margin + 5, yPos); yPos += l.length * 4;
        });
        if (roi.risk_note) { const l = pdf.splitTextToSize(`Watch: ${roi.risk_note}`, maxTextWidth - 5); pdf.text(l, margin + 5, yPos); yPos += l.length * 4; }
        yPos += 4;
      }

      // ===== PROOF OF SKILL =====
      if (selectedSections.proof && allData.intel?.proof) {
        const pr = allData.intel.proof;
        yPos += 6;
        addSectionHeader('Proof Of Skill', [13, 148, 136]);
        pdf.setFontSize(9); pdf.setFont('helvetica', 'normal');
        pdf.text(`Average proof level ${pr.average_level}/7  ·  ${pr.proven_count} documented or better  ·  ${pr.verified_count} credentialed or better`, margin, yPos);
        yPos += 5;
        (pr.skills || []).slice(0, 14).forEach(sk => {
          pdf.text(`${sk.name}: L${sk.level} ${sk.label}${sk.evidence_count ? ` (${sk.evidence_count} evidence)` : ''}`, margin + 5, yPos);
          yPos += 4;
        });
        yPos += 4;
      }

      // ===== LIFESTYLE & FINANCIAL PLAN =====
      if (selectedSections.financial_plan) {
        const calc = allData.calculator;
        if (calc) {
          yPos += 10;
          checkPageBreak(60);
          addSectionHeader('Lifestyle & Financial Plan', [16, 185, 129]);

          const careerTitle = calc.salary?.careerTitle || calc.lifestyle?.career || 'Your chosen lifestyle';
          const monthly = calc.expenses?.total;
          const required = calc.salary?.required ?? calc.analysis?.required_salary;

          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'bold');
          pdf.text(`Target: ${careerTitle}`, margin, yPos);
          yPos += 7;

          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
          if (typeof monthly === 'number') {
            pdf.text(`Estimated monthly cost of living: $${Math.round(monthly).toLocaleString()}`, margin, yPos);
            yPos += 5;
          }
          if (typeof required === 'number') {
            pdf.text(`Salary needed to support this lifestyle: $${Math.round(required).toLocaleString()}/yr`, margin, yPos);
            yPos += 5;
          }
          if (calc.salary?.entry) {
            pdf.text(`Entry salary: $${Math.round(calc.salary.entry).toLocaleString()}/yr  |  Mid: $${Math.round(calc.salary.mid || 0).toLocaleString()}/yr  |  Senior: $${Math.round(calc.salary.senior || 0).toLocaleString()}/yr`, margin, yPos);
            yPos += 5;
          }
          if (calc.analysis?.summary) {
            const sumLines = pdf.splitTextToSize(calc.analysis.summary, maxTextWidth);
            pdf.text(sumLines, margin, yPos);
            yPos += sumLines.length * 4 + 3;
          }
          // Expense breakdown
          if (calc.expenses && typeof calc.expenses === 'object') {
            const rows = ['housing', 'transport', 'food', 'utilities', 'entertainment', 'savings', 'loans']
              .filter((k) => typeof calc.expenses[k] === 'number' && calc.expenses[k] > 0);
            if (rows.length) {
              pdf.setFont('helvetica', 'bold');
              pdf.text('Monthly breakdown:', margin, yPos);
              yPos += 5;
              pdf.setFont('helvetica', 'normal');
              rows.forEach((k) => {
                checkPageBreak(6);
                pdf.text(`- ${k.charAt(0).toUpperCase() + k.slice(1)}: $${Math.round(calc.expenses[k]).toLocaleString()}/mo`, margin + 5, yPos);
                yPos += 4.5;
              });
              yPos += 2;
            }
          }
        }
      }

      // ===== TOP 3 PATHWAYS =====
      if (selectedSections.pathways) {
        const pathways = allData.pathways?.data;
        if (pathways?.paths?.length > 0) {
          addNewPage();
          addSectionHeader('Top 3 Career Pathways', [37, 99, 235]);
          
          pathways.paths.forEach((path, index) => {
            checkPageBreak(35);
            
            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'bold');
            const titleText = `${index + 1}. ${path.title}`;
            const titleLines = pdf.splitTextToSize(titleText, maxTextWidth - 40);
            pdf.text(titleLines, margin, yPos);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(37, 99, 235);
            pdf.text(`${path.match_score}% match`, pageWidth - margin - 25, yPos);
            pdf.setTextColor(30, 41, 59);
            yPos += titleLines.length * 5 + 3;
            
            pdf.setFontSize(9);
            const infoText = `Type: ${path.type || 'N/A'} | Timeline: ${path.timeline || 'N/A'} | Cost: ${path.estimated_cost || 'N/A'}`;
            const infoLines = pdf.splitTextToSize(infoText, maxTextWidth - 10);
            pdf.text(infoLines, margin + 5, yPos);
            yPos += infoLines.length * 4 + 2;
            
            if (path.description) {
              const descLines = pdf.splitTextToSize(path.description, maxTextWidth - 10);
              pdf.text(descLines.slice(0, 3), margin + 5, yPos);
              yPos += Math.min(descLines.length, 3) * 4 + 8;
            }
          });
          
          if (pathways.summary) {
            yPos += 3;
            pdf.setFillColor(240, 249, 255);
            const summaryLines = pdf.splitTextToSize(pathways.summary, maxTextWidth - 15);
            const boxHeight = summaryLines.length * 4 + 8;
            pdf.rect(margin, yPos - 3, maxTextWidth, boxHeight, 'F');
            pdf.setFontSize(9);
            pdf.setFont('helvetica', 'italic');
            pdf.text(summaryLines, margin + 5, yPos + 3);
            yPos += boxHeight + 5;
          }
        }
      }

      // ===== SCHOLARSHIPS =====
      if (selectedSections.scholarships) {
        const scholarships = allData.scholarships?.data;
        if (scholarships?.scholarships?.length > 0) {
          addNewPage();
          addSectionHeader('Scholarship Matches', [22, 163, 74]);
          
          scholarships.scholarships.forEach((scholarship) => {
            checkPageBreak(20);
            
            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'bold');
            const nameLines = pdf.splitTextToSize(scholarship.name || 'Scholarship', maxTextWidth - 40);
            pdf.text(nameLines, margin, yPos);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(22, 163, 74);
            pdf.text(scholarship.amount || 'TBD', pageWidth - margin - 25, yPos);
            pdf.setTextColor(30, 41, 59);
            yPos += nameLines.length * 5 + 2;
            
            pdf.setFontSize(9);
            const detailText = `Deadline: ${scholarship.deadline || 'N/A'} | Match: ${scholarship.match_score || 'N/A'}%`;
            pdf.text(detailText, margin + 5, yPos);
            yPos += 8;
          });
          
          if (scholarships.total_potential) {
            yPos += 5;
            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(22, 163, 74);
            pdf.text(`Total Potential: ${scholarships.total_potential}`, margin, yPos);
            pdf.setTextColor(30, 41, 59);
            yPos += 10;
          }
        }
      }

      // ===== COLLEGE RECOMMENDATIONS =====
      if (selectedSections.colleges) {
        const colleges = allData.colleges?.data;
        if (colleges?.colleges?.length > 0) {
          addNewPage();
          addSectionHeader('College Recommendations', [190, 18, 60]);
          
          colleges.colleges.forEach((college) => {
            checkPageBreak(18);
            
            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'bold');
            const collegeNameLines = pdf.splitTextToSize(college.name || 'College', maxTextWidth - 40);
            pdf.text(collegeNameLines, margin, yPos);
            pdf.setFont('helvetica', 'normal');
            pdf.text(`${college.match_score || 'N/A'}% match`, pageWidth - margin - 25, yPos);
            yPos += collegeNameLines.length * 5 + 2;
            
            pdf.setFontSize(9);
            const collegeInfo = `${college.location || 'N/A'} | Tuition: ${college.tuition || 'N/A'} | Acceptance: ${college.acceptance_rate || 'N/A'}`;
            const collegeInfoLines = pdf.splitTextToSize(collegeInfo, maxTextWidth - 10);
            pdf.text(collegeInfoLines, margin + 5, yPos);
            yPos += collegeInfoLines.length * 4 + 6;
          });
        }
      }

      // ===== FAVORITE COLLEGES =====
      if (selectedSections.favoriteColleges && favoriteColleges.length > 0) {
        addNewPage();
        addSectionHeader('My Favorite Colleges', [236, 72, 153]);
        
        favoriteColleges.forEach((college) => {
          checkPageBreak(15);
          
          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'bold');
          const favNameLines = pdf.splitTextToSize(college.name || 'College', maxTextWidth);
          pdf.text(favNameLines, margin, yPos);
          pdf.setFont('helvetica', 'normal');
          yPos += favNameLines.length * 5 + 2;
          
          pdf.setFontSize(9);
          const details = [
            college.location,
            college.tuition ? `Tuition: ${college.tuition}` : null,
            college.acceptance_rate ? `Acceptance: ${college.acceptance_rate}` : null
          ].filter(Boolean).join(' | ');
          const detailLines = pdf.splitTextToSize(details, maxTextWidth - 10);
          pdf.text(detailLines, margin + 5, yPos);
          yPos += detailLines.length * 4 + 6;
        });
      }

      // ===== MY GOALS =====
      if (selectedSections.goals && goals.length > 0) {
        addNewPage();
        addSectionHeader('My Goals', [245, 158, 11]);
        
        const goalsByStatus = {
          done: goals.filter(g => g.status === 'done'),
          in_progress: goals.filter(g => g.status === 'in_progress'),
          backlog: goals.filter(g => g.status === 'backlog'),
        };
        
        [
          { status: 'done', label: 'Completed', color: [22, 163, 74] },
          { status: 'in_progress', label: 'In Progress', color: [37, 99, 235] },
          { status: 'backlog', label: 'Backlog', color: [148, 163, 184] },
        ].forEach(({ status, label, color }) => {
          if (goalsByStatus[status].length > 0) {
            checkPageBreak(15);
            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(...color);
            pdf.text(`${label} (${goalsByStatus[status].length})`, margin, yPos);
            pdf.setTextColor(30, 41, 59);
            yPos += 6;
            
            goalsByStatus[status].forEach(goal => {
              checkPageBreak(10);
              pdf.setFontSize(9);
              pdf.setFont('helvetica', 'normal');
              const goalTitle = `• ${goal.title}`;
              const goalTitleLines = pdf.splitTextToSize(goalTitle, maxTextWidth - 30);
              pdf.text(goalTitleLines, margin + 5, yPos);
              if (goal.priority) {
                pdf.setTextColor(148, 163, 184);
                pdf.text(`[${goal.priority}]`, pageWidth - margin - 20, yPos);
                pdf.setTextColor(30, 41, 59);
              }
              yPos += goalTitleLines.length * 4 + 2;
            });
            yPos += 5;
          }
        });
      }

      // ===== 7-DAY ACTION PLAN =====
      if (selectedSections.action_plan) {
        const actionPlan = allData.action_plan?.data;
        if (actionPlan?.actions?.length > 0) {
          addNewPage();
          addSectionHeader('7-Day Action Plan', [234, 179, 8]);
          
          if (actionPlan.week_focus) {
            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'bold');
            const focusLines = pdf.splitTextToSize(`Focus: ${actionPlan.week_focus}`, maxTextWidth);
            pdf.text(focusLines, margin, yPos);
            yPos += focusLines.length * 5 + 5;
          }
          
          actionPlan.actions.forEach((action) => {
            checkPageBreak(18);
            
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            const actionTitle = `Day ${action.day}: ${action.title}`;
            const actionTitleLines = pdf.splitTextToSize(actionTitle, maxTextWidth);
            pdf.text(actionTitleLines, margin, yPos);
            yPos += actionTitleLines.length * 4 + 2;
            
            pdf.setFontSize(9);
            pdf.setFont('helvetica', 'normal');
            const actionDesc = pdf.splitTextToSize(action.description || '', maxTextWidth - 10);
            pdf.text(actionDesc.slice(0, 2), margin + 5, yPos);
            yPos += Math.min(actionDesc.length, 2) * 4 + 2;
            
            pdf.text(`Time: ${action.time_estimate || 'N/A'} | Priority: ${action.priority || 'N/A'}`, margin + 5, yPos);
            yPos += 7;
          });
        }
      }

      // ===== 90-DAY ROADMAP =====
      if (selectedSections.roadmap_90day) {
        const roadmap90 = allData.roadmap_90day?.data;
        if (roadmap90?.months?.length > 0) {
          addNewPage();
          addSectionHeader('90-Day Roadmap', [16, 185, 129]);
          
          if (roadmap90.quarter_focus) {
            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'bold');
            const qFocusLines = pdf.splitTextToSize(`Quarter Focus: ${roadmap90.quarter_focus}`, maxTextWidth);
            pdf.text(qFocusLines, margin, yPos);
            yPos += qFocusLines.length * 5 + 5;
          }
          
          roadmap90.months.forEach((month) => {
            checkPageBreak(25);
            
            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'bold');
            const monthTitle = `Month ${month.month}: ${month.theme || 'Focus Month'}`;
            const monthTitleLines = pdf.splitTextToSize(monthTitle, maxTextWidth);
            pdf.text(monthTitleLines, margin, yPos);
            yPos += monthTitleLines.length * 5 + 3;
            
            pdf.setFontSize(9);
            pdf.setFont('helvetica', 'normal');
            if (month.goals?.length > 0) {
              const goalsText = `Goals: ${month.goals.slice(0, 3).join(', ')}`;
              const goalsLines = pdf.splitTextToSize(goalsText, maxTextWidth - 10);
              pdf.text(goalsLines, margin + 5, yPos);
              yPos += goalsLines.length * 4 + 5;
            }
          });
        }
      }

      // ===== 12-MONTH ROADMAP =====
      if (selectedSections.roadmap_12month) {
        const roadmap12 = allData.roadmap_12month?.data;
        if (roadmap12?.quarters?.length > 0) {
          addNewPage();
          addSectionHeader('12-Month Strategic Roadmap', [147, 51, 234]);
          
          if (roadmap12.year_vision) {
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'italic');
            const visionLines = pdf.splitTextToSize(roadmap12.year_vision, maxTextWidth);
            pdf.text(visionLines.slice(0, 3), margin, yPos);
            yPos += Math.min(visionLines.length, 3) * 4 + 8;
          }
          
          roadmap12.quarters.forEach((quarter) => {
            checkPageBreak(25);
            
            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'bold');
            const qName = quarter.name?.replace(/Q\d:\s*/, '') || 'Quarter';
            const quarterTitle = `Q${quarter.quarter}: ${qName}`;
            const quarterTitleLines = pdf.splitTextToSize(quarterTitle, maxTextWidth);
            pdf.text(quarterTitleLines, margin, yPos);
            yPos += quarterTitleLines.length * 5 + 3;
            
            pdf.setFontSize(9);
            pdf.setFont('helvetica', 'normal');
            if (quarter.primary_focus) {
              const focusText = `Focus: ${quarter.primary_focus}`;
              const focusLines = pdf.splitTextToSize(focusText, maxTextWidth - 10);
              pdf.text(focusLines, margin + 5, yPos);
              yPos += focusLines.length * 4 + 2;
            }
            if (quarter.key_actions?.length > 0) {
              const actionsText = `Actions: ${quarter.key_actions.slice(0, 3).join(', ')}`;
              const actionsLines = pdf.splitTextToSize(actionsText, maxTextWidth - 10);
              pdf.text(actionsLines, margin + 5, yPos);
              yPos += actionsLines.length * 4 + 5;
            }
          });
        }
      }

      // ===== FOOTER =====
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(148, 163, 184);
        if (i > 1) {
          pdf.text(
            `Page ${i - 1} of ${totalPages - 1} | Agentic FuturePath AI - Your Career Navigation Partner`,
            pageWidth / 2,
            pageHeight - 10,
            { align: 'center' }
          );
        }
      }

      pdf.save(`FuturePath_Complete_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('Comprehensive report downloaded!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate report. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      toast.success('Report link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  // Report sections for students
  const studentReportSections = [
    { key: 'profile', label: 'Student Profile', icon: Users, color: 'blue', hasData: true },
    { key: 'personality', label: 'Personality & Assessment', icon: Brain, color: 'purple', hasData: allData.personality?.data || allData.assessment },
    { key: 'life_design', label: 'Life Design vs Career Fit', icon: Calculator, color: 'purple', hasData: !!allData.intel?.life_design },
    { key: 'launch_readiness', label: 'Career Launch Readiness', icon: Zap, color: 'cyan', hasData: !!allData.intel?.readiness },
    { key: 'brand_network', label: 'Brand, Visibility & Network', icon: Award, color: 'purple', hasData: !!(allData.intel?.positioning || allData.intel?.network) },
    { key: 'radar', label: 'Opportunity Radar & Risk', icon: AlertCircle, color: 'orange', hasData: !!allData.intel?.radar },
    { key: 'future_self', label: 'Paths To My Future Self', icon: Sparkles, color: 'blue', hasData: !!allData.intel?.future_self },
    { key: 'learning_roi', label: 'Learning ROI', icon: Calculator, color: 'green', hasData: !!allData.intel?.learning_roi },
    { key: 'proof', label: 'Proof Of Skill', icon: BadgeCheck, color: 'teal', hasData: !!allData.intel?.proof },
    { key: 'financial_plan', label: 'Lifestyle & Financial Plan', icon: DollarSign, color: 'emerald', hasData: !!allData.calculator },
    { key: 'future_outlook', label: 'Future Financial Outlook', icon: TrendingUp, color: 'cyan', hasData: true },
    { key: 'careers', label: 'Career Matches', icon: Briefcase, color: 'blue', hasData: true },
    { key: 'pathways', label: 'Career Pathways', icon: Target, color: 'blue', hasData: allData.pathways?.data },
    { key: 'scholarships', label: 'Scholarships', icon: DollarSign, color: 'emerald', hasData: allData.scholarships?.data },
    { key: 'colleges', label: 'College Recommendations', icon: GraduationCap, color: 'rose', hasData: allData.colleges?.data },
    { key: 'favoriteColleges', label: 'Favorite Colleges', icon: Heart, color: 'pink', hasData: favoriteColleges.length > 0 },
    { key: 'experiences', label: 'Internships & Experiences', icon: Briefcase, color: 'teal', hasData: true },
    { key: 'skills', label: 'Readiness & Skills', icon: Shield, color: 'emerald', hasData: true },
    { key: 'financial_literacy', label: 'Financial Literacy', icon: DollarSign, color: 'amber', hasData: true },
    { key: 'decision_lab', label: 'Decision Lab', icon: Brain, color: 'purple', hasData: true },
    { key: 'goals', label: 'My Goals', icon: Rocket, color: 'amber', hasData: goals.length > 0 },
    { key: 'action_plan', label: '7-Day Action Plan', icon: Calendar, color: 'amber', hasData: allData.action_plan?.data },
    { key: 'roadmap_90day', label: '90-Day Roadmap', icon: MapPin, color: 'teal', hasData: allData.roadmap_90day?.data },
    { key: 'roadmap_12month', label: '12-Month Strategic Plan', icon: BookOpen, color: 'purple', hasData: allData.roadmap_12month?.data },
  ];

  // Report sections for parents
  const parentReportSections = [
    { key: 'family_overview', label: 'Family Overview', icon: Users, color: 'pink', hasData: true },
    { key: 'children_summary', label: 'Children Summary', icon: GraduationCap, color: 'blue', hasData: children.length > 0 },
    { key: 'financial_overview', label: 'Financial Planning', icon: DollarSign, color: 'emerald', hasData: true },
    { key: 'scholarships', label: 'Scholarship Tracking', icon: Award, color: 'amber', hasData: true },
    { key: 'milestones', label: 'Upcoming Milestones', icon: Calendar, color: 'purple', hasData: true },
  ];

  const reportSections = isParent ? parentReportSections : studentReportSections;

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-96">
          <LoadingSpinner size="lg" />
        </div>
      </AppLayout>
    );
  }

  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="report-arrival"
        eyebrow="My FuturePath Report"
        title="Everything you decided, on paper."
        subtitle="One document that pulls it all together, who you are, the life you designed, the path you chose, the money behind it and your next steps. Built for the conversation with the people who need convincing."
        accent="#fbbf24"
        second="#a78bfa"
        ctaLabel="Build my report"
        narration={"This is your FuturePath Report. Everything you have done here becomes one document: your assessment, the life you designed and what it costs, the path you chose and why, the funding behind it, and the dated steps you are taking next. It is written to be handed to somebody, a parent, a counsellor, an employer, a lender, because a plan people can read is a plan people support."}
        facts={[
          { icon: A1, head: "One document", sub: "Assessment, life, path, money, next steps" },
          { icon: A3, head: "Built to share", sub: "For parents, counsellors, employers or lenders" },
          { icon: A2, head: "Yours to keep", sub: "Download it any time, in your name" }
        ]}
        modules={[
          { icon: A4, label: "Who you are", line: "Your interests, personality and strengths", hue: "#a78bfa" },
          { icon: A6, label: "The life you designed", line: "Your number, and what it is made of", hue: "#34d399" },
          { icon: A5, label: "Your chosen path", line: "The decision, and the reasoning behind it", hue: "#22d3ee" },
          { icon: A6, label: "The money", line: "Costs, funding, borrowing and repayment", hue: "#fbbf24" },
          { icon: A1, label: "Your next steps", line: "The dated actions you have committed to", hue: "#f472b6" },
          { icon: A2, label: "Export", line: "A clean PDF, ready to hand over", hue: "#60a5fa" }
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  return (
    <AppLayout>
      {/* Back Button */}
      <div className="mb-4">
        <Button
          variant="ghost"
          onClick={() => navigate(isParent ? '/app/family' : '/app/dashboard')}
          className="text-slate-300 hover:text-white hover:bg-slate-800/60 gap-2"
          data-testid="back-to-dashboard-btn"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to {isParent ? 'Family Dashboard' : 'Dashboard'}
        </Button>
      </div>

      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl mb-8 p-8 bg-gradient-to-r from-blue-900 via-purple-900 to-pink-900">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl" />
          <div className="absolute top-1/2 right-0 w-48 h-48 bg-pink-500/20 rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 flex items-center justify-between flex-wrap gap-6">
          <div className="flex items-center gap-5">
            <motion.div 
              className="w-20 h-20 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/30"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <FileText className="w-10 h-10 text-white" />
            </motion.div>
            <div>
              <motion.h1 
                className="font-outfit text-3xl font-bold text-white"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                {isParent ? 'My FuturePath Report' : 'My FuturePath Report'}
              </motion.h1>
              <motion.p 
                className="text-blue-100 mt-1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                {isParent 
                  ? "Your family's complete FuturePath story, pick the sections you want and download a polished, shareable PDF covering each child's pathways, financials, scholarships and milestones."
                  : "Your complete FuturePath story in one place, choose the sections to include and download a polished, shareable PDF with your personality, career pathways, financial plan, scholarships and action steps."
                }
              </motion.p>
            </div>
          </div>
          
          {/* Quick Stats */}
          <div className="flex gap-4">
            {isParent ? (
              <>
                <div className="text-center px-4 py-2 bg-white/15 rounded-xl backdrop-blur-sm border border-white/10">
                  <p className="text-2xl font-bold text-white">{children.length}</p>
                  <p className="text-xs text-blue-100">Children</p>
                </div>
              </>
            ) : (
              <>
                <div className="text-center px-4 py-2 bg-white/15 rounded-xl backdrop-blur-sm border border-white/10">
                  <p className="text-2xl font-bold text-white">{goals.length}</p>
                  <p className="text-xs text-blue-100">Goals</p>
                </div>
                <div className="text-center px-4 py-2 bg-white/15 rounded-xl backdrop-blur-sm border border-white/10">
                  <p className="text-2xl font-bold text-white">{favoriteColleges.length}</p>
                  <p className="text-xs text-blue-100">Favorites</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <GlassCard>
            {/* Select All / Deselect All */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-700">
              <div>
                <h2 className="text-lg font-semibold text-white">Select Sections to Include</h2>
                <p className="text-sm text-slate-300 mt-1">
                  {reportSections.filter(s => s.hasData && selectedSections[s.key]).length} of {reportSections.filter(s => s.hasData).length} sections selected
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectAll}
                  disabled={allSelected}
                  className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  data-testid="select-all-btn"
                >
                  <CheckSquare className="w-4 h-4 mr-2" />
                  All
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={deselectAll}
                  disabled={noneSelected}
                  className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  data-testid="deselect-all-btn"
                >
                  <Square className="w-4 h-4 mr-2" />
                  None
                </Button>
              </div>
            </div>

            {/* Selectable Sections */}
            <div className="grid sm:grid-cols-2 gap-3 mb-6">
              {reportSections.map((section, index) => {
                const Icon = section.icon;
                const isSelected = selectedSections[section.key];
                const hasData = section.hasData;
                
                return (
                  <motion.button
                    key={section.key}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => toggleSection(section.key)}
                    disabled={!hasData}
                    data-testid={`section-${section.key}`}
                    className={`p-4 rounded-xl border text-left transition-all ${
                      !hasData 
                        ? 'bg-slate-800/20 border-slate-700/30 cursor-not-allowed opacity-50'
                        : isSelected 
                          ? 'bg-gradient-to-r from-blue-600/20 to-purple-600/20 border-blue-500/50 hover:border-blue-400' 
                          : 'bg-slate-800/30 border-slate-700/50 hover:border-slate-600'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                        isSelected && hasData 
                          ? 'bg-gradient-to-br from-blue-500 to-purple-500' 
                          : 'bg-slate-700'
                      }`}>
                        {isSelected && hasData ? (
                          <Check className="w-5 h-5 text-white" />
                        ) : (
                          <Icon className={`w-5 h-5 ${hasData ? 'text-slate-300' : 'text-slate-300'}`} />
                        )}
                      </div>
                      <div className="flex-1">
                        <span className={`font-medium ${hasData ? 'text-white' : 'text-slate-300'}`}>
                          {section.label}
                        </span>
                        {!hasData && (
                          <span className="text-xs text-slate-300 block">Not generated yet</span>
                        )}
                      </div>
                      {hasData && (
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                          isSelected 
                            ? 'bg-gradient-to-br from-blue-500 to-purple-500 border-transparent' 
                            : 'border-slate-600 hover:border-slate-500'
                        }`}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                      )}
                    </div>
                  </motion.button>
                );
              })}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              {!isParent && (
                <Button
                  onClick={openPreview}
                  disabled={generating || noneSelected}
                  variant="outline"
                  className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 py-6 text-lg font-semibold"
                  data-testid="preview-report-btn"
                >
                  {generating ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Eye className="w-5 h-5 mr-2" />}
                  Preview
                </Button>
              )}
              <Button
                onClick={isParent ? generateParentPDF : generateStudentPDF}
                disabled={generating || noneSelected}
                className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 hover:from-blue-700 hover:via-purple-700 hover:to-pink-700 flex-1 sm:flex-none py-6 text-lg font-semibold shadow-lg shadow-purple-500/25"
                data-testid="download-report-btn"
              >
                {generating ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Download className="w-5 h-5 mr-2" />
                )}
                Download Report
              </Button>

              <Button
                onClick={handleCopyLink}
                variant="outline"
                className="border-slate-600 text-slate-300 hover:bg-slate-800 py-6"
              >
                {copied ? <Check className="w-5 h-5 mr-2 text-emerald-400" /> : <Link2 className="w-5 h-5 mr-2" />}
                {copied ? 'Copied!' : 'Copy Link'}
              </Button>
            </div>
          </GlassCard>

          {/* Report History (students) */}
          {!isParent && (
            <GlassCard className="mt-6" data-testid="report-history-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center">
                  <History className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-outfit text-lg font-semibold text-white">Report History</h3>
                  <p className="text-sm text-slate-300">Revisit and re-download reports you've generated before.</p>
                </div>
              </div>

              {history.length === 0 ? (
                <div className="text-center py-8 border border-dashed border-slate-700 rounded-xl" data-testid="report-history-empty">
                  <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-300 text-sm">No reports yet, download one and it'll appear here.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {history.map((h, index) => (
                    <motion.div
                      key={h.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.04 }}
                      className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/40 border border-slate-700/50 hover:border-cyan-500/40 transition-colors"
                      data-testid={`report-history-item-${index}`}
                    >
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-600/30 to-cyan-500/30 flex items-center justify-center shrink-0">
                        <FileText className="w-5 h-5 text-cyan-300" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium text-sm truncate">{h.title}</p>
                        <div className="flex items-center gap-3 text-xs text-slate-300 mt-0.5">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(h.generated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                          <span>{h.stats?.sectionCount ?? (h.sections || []).length} sections</span>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => previewHistoryItem(h.id)}
                        className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"
                        data-testid={`history-preview-${index}`}
                      >
                        <Eye className="w-4 h-4 sm:mr-1.5" />
                        <span className="hidden sm:inline">View</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteHistoryItem(h.id)}
                        className="text-slate-300 hover:text-red-400 hover:bg-red-500/10"
                        data-testid={`history-delete-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </motion.div>
                  ))}
                </div>
              )}
            </GlassCard>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* What's Included Card */}
          <GlassCard className="border-purple-500/30">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-outfit text-lg font-semibold text-white">Report Features</h3>
            </div>
            <ul className="space-y-3">
              {(isParent ? [
                { icon: Users, text: 'Family Overview', color: 'pink' },
                { icon: GraduationCap, text: 'Children Progress', color: 'blue' },
                { icon: DollarSign, text: 'Financial Summary', color: 'emerald' },
                { icon: Calendar, text: 'Upcoming Milestones', color: 'amber' },
              ] : [
                { icon: Brain, text: 'Personality Analysis', color: 'purple' },
                { icon: DollarSign, text: 'Lifestyle & Financial Plan', color: 'emerald' },
                { icon: Target, text: 'Career Pathways', color: 'blue' },
                { icon: DollarSign, text: 'Scholarships', color: 'emerald' },
                { icon: Rocket, text: 'Goals & Plans', color: 'amber' },
              ]).map((item, index) => (
                <motion.li 
                  key={item.text}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="flex items-center gap-3 text-sm"
                >
                  <div className={`w-8 h-8 rounded-lg bg-${item.color}-500/20 flex items-center justify-center`}>
                    <item.icon className={`w-4 h-4 text-${item.color}-400`} />
                  </div>
                  <span className="text-slate-300">{item.text}</span>
                </motion.li>
              ))}
            </ul>
          </GlassCard>

          {/* Tips Card */}
          <GlassCard className="border-blue-500/30">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                <Star className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-outfit text-lg font-semibold text-white">Pro Tips</h3>
            </div>
            <ul className="space-y-3 text-sm text-slate-300">
              <li className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                <span>Share your report with counselors or advisors</span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                <span>Generate new reports as you complete more sections</span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                <span>Keep reports for tracking progress over time</span>
              </li>
            </ul>
          </GlassCard>
        </div>
      </div>

      <ReportPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        data={previewData}
        sectionKeys={previewKeys}
        onDownloaded={handlePreviewDownloaded}
      />
    </AppLayout>
  );
}
